import type { WidgetOptionValue } from '../../../store/dashboardStore';
import { useMenuEnabledFn } from '../../../lib/menu-gate';
import { useWidgetChrome } from './widget-chrome';
import {
  getItemSchema, isItemVisible, resolveOption, visibleItemKeys, type ItemContext,
} from './widget-items';

/**
 * 위젯이 «제 자리의 보이는 항목 · 선택지»를 읽는 훅 (2026-09-16 · 대시보드 시안 v2 V3a).
 *
 * ★★ 규칙은 V1 모듈이 정한다 — 위젯은 「이 항목을 그릴까」 · 「이 선택지 값은?」만 묻는다.
 * ★ 막힘 재료는 설정 창과 **같게** 넣는다(메뉴 켜짐만 · 범위 · 서버 기능은 비움) — 창에서 막힌 것이 위젯에 새지 않게.
 * ★ 설정이 없는 자리는 기본값 = 지금 그리던 것(V1 규칙 D1).
 */
export interface WidgetItemsView {
  visible: (group?: string) => string[];
  has: (key: string) => boolean;
  option: <T extends WidgetOptionValue = WidgetOptionValue>(key: string) => T | undefined;
}

export function useWidgetItems(widgetKey: string): WidgetItemsView {
  const { settings } = useWidgetChrome();
  const menuEnabled = useMenuEnabledFn();
  const schema = getItemSchema(widgetKey);
  const ctx: ItemContext = { menuEnabled };
  if (!schema) {
    return { visible: () => [], has: () => false, option: () => undefined };
  }
  return {
    visible: (group) => visibleItemKeys(schema, settings, ctx, group),
    has: (key) => isItemVisible(schema, settings, key, ctx),
    option: <T extends WidgetOptionValue = WidgetOptionValue>(key: string) =>
      resolveOption(schema, settings, key, ctx) as T | undefined,
  };
}
